import { ChangeDetectionStrategy, Component, computed, effect, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OperaCloudSsdPmsSetup, PmsType } from '../../../../../../types/pms';

@Component({
    selector: 'juyo-opera-cloud-ssd-connect',
    styleUrl: './ssd.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
    template: `
        <button type="button" class="back-link" (click)="back.emit()">
            <span class="material-symbols-outlined">arrow_left_alt</span>
            Change connection type
        </button>

        <p class="section-label">SSD CONFIGURATION</p>

        <div class="field-grid">
            <div class="field">
                <label class="field-label" for="ssd-chain-code">CHAIN CODE <span class="required">*</span></label>
                <input
                    id="ssd-chain-code"
                    type="text"
                    class="field-input"
                    placeholder="e.g. JUYOCHAIN"
                    [ngModel]="chainCode()"
                    (ngModelChange)="chainCode.set($event)"
                />
                <p class="field-hint">SSD Admin Portal &rarr; Identity Self Service &rarr; Organizations</p>
            </div>

            <div class="field">
                <label class="field-label" for="ssd-property-code">PROPERTY CODE <span class="required">*</span></label>
                <input
                    id="ssd-property-code"
                    type="text"
                    class="field-input"
                    placeholder="e.g. LON01"
                    [ngModel]="propertyCode()"
                    (ngModelChange)="propertyCode.set($event)"
                />
                <p class="field-hint">Top-right header of Opera Cloud</p>
            </div>
        </div>

        <div class="field">
            <label class="field-label" for="ssd-login-link">HOTEL LOGIN LINK <span class="required">*</span></label>
            <input
                id="ssd-login-link"
                type="text"
                class="field-input"
                placeholder="https://xxxx-prod2-ssd-ohs.oracleindustry.com/identity/faces/home"
                [ngModel]="hotelLoginLink()"
                (ngModelChange)="hotelLoginLink.set($event)"
            />
            <p class="field-hint">Paste the URL you use to log into Opera Cloud.</p>
        </div>

        <div class="field">
            <label class="field-label" for="ssd-system-code">
                EXTERNAL SYSTEM CODE <span class="required">*</span>
            </label>
            <input
                id="ssd-system-code"
                type="text"
                class="field-input"
                placeholder="JUYOOHIP"
                [ngModel]="externalSystemCode()"
                (ngModelChange)="externalSystemCode.set($event)"
            />
            <p class="field-hint">e.g. JUYOOHIP, JUYOOHIP2 — the exact code you created or linked in Opera Cloud.</p>
        </div>

        <button
            type="button"
            class="setup-toggle"
            (click)="showSetup.set(!showSetup())"
            [attr.aria-expanded]="showSetup()"
            aria-controls="ssd-setup-panel"
        >
            <span class="material-symbols-outlined">{{ showSetup() ? 'expand_more' : 'chevron_right' }}</span>
            Show me how to set this up in Opera Cloud
        </button>

        @if (showSetup()) {
            <div class="setup-panel" id="ssd-setup-panel">
                <p class="setup-intro">
                    Before we can receive data, you'll need to create or link the
                    <strong>{{ systemCode() }} External System</strong> in Opera Cloud so business events can publish to
                    Juyo.
                </p>

                <p class="setup-heading">Standard procedure</p>
                <ol class="setup-steps">
                    <li>
                        In Opera Cloud, open
                        <strong>Navigator › Administration › Interfaces › Business Events › External Systems</strong>
                    </li>
                    <li>
                        Click the three dots (⋮) next to <strong>New</strong> and select <strong>Show Inactive</strong>
                    </li>
                    <li>
                        Scroll the list and locate <strong>{{ systemCode() }}</strong>
                    </li>
                    <li>
                        Open it → under <strong>Properties</strong>, find your Property Code, tick
                        <strong>Active</strong>, and Save
                    </li>
                </ol>

                <p class="setup-heading">If {{ systemCode() }} doesn't exist</p>
                <ol class="setup-steps">
                    <li>In the same screen, click <strong>New</strong></li>
                    <li>Under <strong>Code</strong>, enter a unique code (e.g. JUYOOHIP, JUYOOHIP1...)</li>
                    <li>In <strong>Sequence</strong>, enter any number (e.g. 15). Leave other fields blank.</li>
                    <li>
                        Under <strong>Property</strong>, find your Property Code, tick <strong>Active</strong>, and Save
                    </li>
                    <li>Open ⋮ beside the new system → <strong>Publishers</strong> → add your channel manager</li>
                </ol>

                <p class="setup-tip">
                    <span class="tip-label">Tip:</span> If your property is in a chain, check whether it already exists
                    at chain level first.
                </p>

                <div class="setup-divider"></div>

                <p class="setup-outro">
                    Once you've completed the setup above, paste the exact code you used into the
                    <strong>External System Code</strong> field so we can match data on our side.
                </p>
            </div>
        }

        <div class="next-box">
            <span class="material-symbols-outlined next-icon">info</span>
            <div class="next-content">
                <p class="next-title">What happens next</p>
                <p class="next-body">
                    After you submit, Juyo will create a vendor user in your SSD chain. You'll then need to: (1) approve
                    it in <strong>SSD Admin Portal &gt; Tasks &gt; Approve Requests</strong>, and (2) assign
                    <strong>CHAIN-WSACCESS</strong> (or HOTEL-WSACCESS) role to that user. We'll email you when the
                    request is ready to approve.
                </p>
            </div>
        </div>

        <p class="guide-link-text">
            Need more detail? Read the
            <a class="guide-link" [href]="guideUrl" target="_blank" rel="noopener noreferrer">
                full Opera Cloud onboarding guide →
            </a>
        </p>
    `,
})
export class OperaCloudSsdConnectComponent {
    public back = output<void>();
    public valueChange = output<OperaCloudSsdPmsSetup | null>();

    protected readonly guideUrl =
        'https://intercom-help.eu/juyo-analytics/en/articles/618454-opera-cloud-onboarding-what-to-expect';

    protected chainCode = signal('');
    protected propertyCode = signal('');
    protected hotelLoginLink = signal('');
    protected externalSystemCode = signal('');
    protected showSetup = signal(false);

    protected systemCode = computed(() => this.externalSystemCode().trim());

    private setup = computed<OperaCloudSsdPmsSetup | null>(() => {
        const chainCode = this.chainCode().trim();
        const propertyCode = this.propertyCode().trim();
        const hotelLoginLink = this.hotelLoginLink().trim();
        const externalSystemCode = this.externalSystemCode().trim();

        if (!chainCode || !propertyCode || !hotelLoginLink || !externalSystemCode) {
            return null;
        }

        return {
            type: PmsType.OperaCloud,
            path: 'SSD',
            chainCode,
            propertyCode,
            hotelLoginLink,
            externalSystemCode,
        };
    });

    constructor() {
        effect(() => this.valueChange.emit(this.setup()));
    }
}
